import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3204';
import { useU3204Context } from '../U3204Context';
import { doGetQuotaStatus, luHandleAppiError, type U3204QuotaStatus } from '../U3204Service';
import { u3204IsQuotaFooterVisible } from '../utils/u3204QuotaUsageVisibility';

export function useU3204QuotaStatus() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const { initLU, activeMission, streamState } = useU3204Context();
  const [quotaStatus, setQuotaStatus] = useState<U3204QuotaStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const previousStreamStateRef = useRef(streamState);

  const refreshQuotaStatus = useCallback(
    async (missionId?: string | null) => {
      const targetMissionId = missionId ?? activeMission?.id ?? null;
      if (!targetMissionId || !initLU.hasUserGroup) {
        setQuotaStatus(null);
        return null;
      }

      setLoading(true);
      try {
        const response = await doGetQuotaStatus(axios, targetMissionId);
        const nextStatus = response.data?.result ?? null;
        setQuotaStatus(nextStatus);
        return nextStatus;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        setQuotaStatus(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [activeMission?.id, axios, initLU.hasUserGroup, t, toast],
  );

  useEffect(() => {
    void refreshQuotaStatus(activeMission?.id ?? null);
  }, [activeMission?.id, refreshQuotaStatus]);

  useEffect(() => {
    const wasStreaming = previousStreamStateRef.current === 'streaming';
    previousStreamStateRef.current = streamState;
    if (wasStreaming && streamState === 'idle') {
      void refreshQuotaStatus(activeMission?.id ?? null);
    }
  }, [activeMission?.id, refreshQuotaStatus, streamState]);

  return {
    quotaStatus,
    loading,
    visible: u3204IsQuotaFooterVisible(quotaStatus),
    blocked: quotaStatus?.blocked ?? false,
    blockReason: quotaStatus?.blockReason ?? null,
    refreshQuotaStatus,
  };
}
